import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { ImgID } from "../../../image_loader/img_id.js";
import type { WinStreakTowerRewardDto } from "../../../api/dto/win_streak_tower.dto.js";
import { Easing } from "../../../transforms/transform_base.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AdventureTooltip } from "../../tooltip/adventure_tooltip.js";
import { TooltipManager } from "../../tooltip/tooltip_manager.js";
import { AdventureBox, AdventureBoxArt } from "./adventure_box_art.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { AdventureBadgeInfo, AdventureBoxState, AdventureFloorInfo, buildAdventureZeroFloor } from "./adventure_floor_info.js";

export class AdventureTower
{
  private readonly ctx: CanvasRenderingContext2D;
  private readonly onClaim: ( floor: AdventureFloorInfo ) => Promise<WinStreakTowerRewardDto[] | null>;
  private readonly level_badges = new Map<number, { content: UIElement; x: number; y: number; elements: UIElement[] }>();

  private static readonly BOTTOM_ANCHOR = 120;
  private static readonly HOUSE_X = 10;

  private static readonly ZERO_W = 494;
  private static readonly ZERO_H = 432;
  private static readonly ZERO_BADGE_X = 214.5;
  private static readonly ZERO_LEVEL_Y = 170.5;

  private static readonly TOP_W = 657;
  private static readonly TOP_H = 725;
  private static readonly TOP_FLOOR_Y = 47.5;
  private static readonly TOP_BOX_Y = 103;
  private static readonly TOP_LEVEL_Y = 504.5;
  private static readonly TOP_WINDOW_X = 181;
  private static readonly TOP_WINDOW_Y = 448;

  private static readonly CENTRE_W = 654;
  private static readonly CENTRE_H = 390;
  private static readonly CENTRE_FLOOR_Y = 41.5;
  private static readonly CENTRE_BOX_Y = 103;
  private static readonly CENTRE_LEVEL_Y = 171.5;
  private static readonly CENTRE_WINDOW_X = 177;
  private static readonly CENTRE_WINDOW_Y = 115;

  private readonly WINDOW_FILL_W = 151;
  private readonly CENTER_FILL_H = 390;
  private readonly TOP_FILL_H = 158;
  private readonly WINDOW_FILL_OVERHANG_X = 3;

  private readonly BOX_CENTER_X_IN_PIECE = 525;
  private readonly BOX_SCALE = 0.55;

  private readonly BOX_BUTTON_W = 277;
  private readonly BOX_BUTTON_H = 147;

  private readonly BADGE_X_IN_PIECE = 217;

  private readonly CHECK_W = 60;
  private readonly CHECK_H = 60;

  private readonly TOOLTIP_TAIL_X_RATIO = 0.7;

  constructor( ctx: CanvasRenderingContext2D, onClaim: ( floor: AdventureFloorInfo ) => Promise<WinStreakTowerRewardDto[] | null> )
  {
    this.ctx = ctx;
    this.onClaim = onClaim;
  }

  static requiredContentHeight( centre_count: number ): number
  {
    const design_heights = [ this.ZERO_H, ...Array( centre_count ).fill( this.CENTRE_H ), this.TOP_H ];
    const stack_h = design_heights.reduce( ( sum, h ) => sum + LayoutScaler.heightFromDesign( h ) - 1, 0 );

    return LayoutScaler.yFromDesign( this.BOTTOM_ANCHOR ) + stack_h;
  }

  buildHouses( content: UIElement, content_h: number, floors: AdventureFloorInfo[] ): AdventureBadgeInfo[]
  {
    let piece_top_y = content_h - LayoutScaler.yFromDesign( AdventureTower.BOTTOM_ANCHOR );
    const badge_infos: AdventureBadgeInfo[] = [];

    const zero_w = LayoutScaler.widthFromDesign( AdventureTower.ZERO_W );
    const zero_h = LayoutScaler.heightFromDesign( AdventureTower.ZERO_H );
    piece_top_y -= zero_h - 1;

    const zero_piece = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( AdventureTower.HOUSE_X ),
      piece_top_y,
      zero_w,
      zero_h,
      ImgID.UI_AdventureZeroFloor
    );
    content.addChild( zero_piece );
    this.buildPieceLabel( zero_piece, AdventureTower.ZERO_H, "LEVEL", 150, 175, 50, 145, 30, true );

    badge_infos.push( {
      x: LayoutScaler.xFromDesign( AdventureTower.HOUSE_X ) + LayoutScaler.xFromDesign( AdventureTower.HOUSE_X + AdventureTower.ZERO_BADGE_X ),
      y: piece_top_y + LayoutScaler.yFromDesign( AdventureTower.ZERO_LEVEL_Y ),
      floor_info: buildAdventureZeroFloor(),
    } );

    const addPiece = ( img_id: ImgID, design_w: number, design_h: number, floor_y: number, floor_number: number, box_bottom_y: number, level_y: number, window_x: number, window_y: number, floor_info: AdventureFloorInfo, is_bottom: boolean = false, is_top: boolean = false ): void =>
    {
      const w = LayoutScaler.widthFromDesign( design_w );
      const h = LayoutScaler.heightFromDesign( design_h );

      piece_top_y -= h - 1;

      const box_center_x = LayoutScaler.xFromDesign( AdventureTower.HOUSE_X + this.BOX_CENTER_X_IN_PIECE );
      const box_bottom_content_y = piece_top_y + h - LayoutScaler.yFromDesign( box_bottom_y );
      const box_state = this.boxStateFor( floor_info );
      const box = AdventureBoxArt.forState( this.ctx, content, box_state, box_center_x, box_bottom_content_y, this.BOX_SCALE );

      const centre_piece_x = LayoutScaler.xFromDesign( AdventureTower.HOUSE_X );
      const centre_piece_w = LayoutScaler.widthFromDesign( AdventureTower.CENTRE_W );
      const top_left_extension = is_top ? 1 : 0;
      const piece_x = centre_piece_x + ( centre_piece_w - w ) / 2 - top_left_extension;
      const piece_w = w + top_left_extension;
      const piece = new UIImage( this.ctx, piece_x, piece_top_y, piece_w, h, img_id );

      this.buildFloorPlaque( h, floor_number, floor_y, piece );

      if ( floor_info.passed )
        this.buildWindowFill( window_x, window_y, piece, is_top );

      if ( is_top )
        this.buildPieceLabel( piece, design_h, "BIG_GIFT", 180, 172, 60, 301, 25 );

      if ( is_bottom )
        this.buildPieceLabel( piece, design_h, "LEVEL", 150, 175, 50, 145, 30, true );

      const badge_x_in_piece = is_top
        ? this.BADGE_X_IN_PIECE
        : this.BADGE_X_IN_PIECE - 1.5;
      const badge_x = LayoutScaler.xFromDesign( AdventureTower.HOUSE_X ) + LayoutScaler.xFromDesign( AdventureTower.HOUSE_X + badge_x_in_piece );
      const badge_y = piece_top_y + LayoutScaler.yFromDesign( level_y );

      badge_infos.push( {
        x: badge_x,
        y: badge_y,
        floor_info
      } );

      content.addChild( piece );

      if ( box_state === AdventureBoxState.Claimable )
        this.buildClaimableButton( content, box, floor_info, box_center_x, box_bottom_content_y );
      else if ( box_state === AdventureBoxState.Locked )
        this.buildLockedTapCatcher( content, box, floor_info, box_center_x, box_bottom_content_y );
    };

    let floor_number = 0;
    const centre_count = Math.max( 0, floors.length - 1 );

    for ( let i = 0; i < centre_count; i++ )
      addPiece( ImgID.UI_AdventureHouseCentre, AdventureTower.CENTRE_W, AdventureTower.CENTRE_H, AdventureTower.CENTRE_FLOOR_Y, ++floor_number, AdventureTower.CENTRE_BOX_Y, AdventureTower.CENTRE_LEVEL_Y, AdventureTower.CENTRE_WINDOW_X, AdventureTower.CENTRE_WINDOW_Y, floors[i] );

    addPiece( ImgID.UI_AdventureHouseTop, AdventureTower.TOP_W, AdventureTower.TOP_H, AdventureTower.TOP_FLOOR_Y, ++floor_number, AdventureTower.TOP_BOX_Y, AdventureTower.TOP_LEVEL_Y, AdventureTower.TOP_WINDOW_X, AdventureTower.TOP_WINDOW_Y, floors[floors.length - 1], false, true );

    return badge_infos;
  }

  buildLevelBadges( content: UIElement, badge_infos: AdventureBadgeInfo[], current_level: number ): void
  {
    this.level_badges.clear();

    for ( let i = 1; i < badge_infos.length; i++ )
    {
      const info = badge_infos[i];
      this.buildLevelBadge( content, info.x, info.y, info.floor_info, current_level >= info.floor_info.level );
    }
  }

  private buildFloorPlaque( block_h: number, floor_number: number, floor_y: number, piece: UIElement ): void
  {
    const floor_bg_w = LayoutScaler.widthFromDesign( 144 );
    const floor_bg_h = LayoutScaler.heightFromDesign( 144 );
    const floor_bg_x = LayoutScaler.xFromDesign( 302 );
    const floor_bg_y = block_h - floor_bg_h - LayoutScaler.yFromDesign( floor_y );

    const floor_bg = new UIImage( this.ctx, floor_bg_x, floor_bg_y, floor_bg_w, floor_bg_h, ImgID.UI_AdventureFloorBg, { rotation_angle: 11 } );

    const floor_num_y = LayoutScaler.yFromDesign( 25 );
    const floor_num_h = LayoutScaler.heightFromDesign( 55 );
    const num_style = {
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_weight: 900,
      font_size: LayoutScaler.textSizeFromDesign( 44 ),
      text_color: ColorScheme.adventure_floor_text,
    };

    floor_bg.addChild( new UITextElement( this.ctx, 0, floor_num_y, floor_bg_w, floor_num_h, {
      text: floor_number.toString(),
      ...num_style,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 6 ),
      outline_color: ColorScheme.white,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent_30
    } ) );
    floor_bg.addChild( new UITextElement( this.ctx, 0, floor_num_y, floor_bg_w, floor_num_h, { text: floor_number.toString(), ...num_style } ) );

    const floor_text_x = LayoutScaler.xFromDesign( 15 );
    const floor_text_y = LayoutScaler.yFromDesign( 82 );
    const floor_text_w = LayoutScaler.widthFromDesign( 108 );
    const floor_text_h = LayoutScaler.heightFromDesign( 28 );
    floor_bg.addChild( new UITextElement( this.ctx, floor_text_x, floor_text_y, floor_text_w, floor_text_h, {
      localization_key: "FLOOR",
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_weight: 900,
      font_size: LayoutScaler.textSizeFromDesign( 22 ),
      text_color: ColorScheme.adventure_floor_text,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 16 )
    } ) );

    piece.addChild( floor_bg );
  }

  private buildWindowFill( window_x: number, window_y: number, piece: UIElement, is_top: boolean ): void
  {
    const w = LayoutScaler.widthFromDesign( this.WINDOW_FILL_W );
    const h = LayoutScaler.heightFromDesign( is_top ? this.TOP_FILL_H : this.CENTER_FILL_H );
    const x = LayoutScaler.xFromDesign(
      is_top ? window_x : window_x - this.WINDOW_FILL_OVERHANG_X
    );
    const y = LayoutScaler.yFromDesign( is_top ? window_y : 0 );
    const img_id = is_top
      ? ImgID.UI_AdventureTowerTopFill
      : ImgID.UI_AdventureTowerCenterFill;

    piece.addChild( new UIImage( this.ctx, x, y, w, h, img_id ) );
  }

  private buildPieceLabel( piece: UIElement, design_h: number, text: string, design_w: number, design_x: number, text_h_design: number, bottom_gap_design: number, font_size_design: number, outlined: boolean = false ): void
  {
    const text_w = LayoutScaler.widthFromDesign( design_w );
    const text_x = LayoutScaler.xFromDesign( design_x );
    const text_h = LayoutScaler.heightFromDesign( text_h_design );
    const text_y = LayoutScaler.heightFromDesign( design_h ) - text_h - LayoutScaler.yFromDesign( bottom_gap_design );

    const base_style = {
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_weight: 700,
      font_size: LayoutScaler.textSizeFromDesign( font_size_design ),
    };

    if ( !outlined )
    {
      piece.addChild( new UITextElement( this.ctx, text_x, text_y, text_w, text_h, { localization_key: text, ...base_style, text_color: ColorScheme.adventure_floor_text } ) );
      return;
    }

    piece.addChild( new UITextElement( this.ctx, text_x, text_y, text_w, text_h, {
      localization_key: text, ...base_style,
      text_color: ColorScheme.white,
      outline_color: ColorScheme.adventure_level_marker_outline,
      outline_width: LayoutScaler.widthFromDesign( 8 ),
      ...TextStyleScheme.buttonShadow(),
    } ) );
    piece.addChild( new UITextElement( this.ctx, text_x, text_y, text_w, text_h, { localization_key: text, ...base_style, text_color: ColorScheme.white } ) );
  }

  private buildClaimableButton( content: UIElement, box: AdventureBox, floor_info: AdventureFloorInfo, center_x: number, center_y: number ): void
  {
    const button_w = LayoutScaler.widthFromDesign( this.BOX_BUTTON_W );
    const button_h = LayoutScaler.heightFromDesign( this.BOX_BUTTON_H );
    const button_y = center_y - button_h / 2;

    const button = new UIImage( this.ctx, center_x - button_w / 2, button_y, button_w, button_h, ImgID.UI_AdventureButton, {}, true );

    const text = new UITextElement( this.ctx, 0, 0, button_w, button_h, {
      localization_key: "OPEN",
      font_size: LayoutScaler.textSizeFromDesign( 32 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );
    button.addChild( text, true );

    content.addChild( button );

    button.setOnClick( async () =>
    {
      button.setClickable( false );
      const rewards = await this.onClaim( floor_info );
      if ( rewards === null )
      {
        button.setClickable( true );
        return;
      }

      floor_info.reward_collected = true;
      this.replaceLevelBadgeWithCheck( floor_info.id );
      this.openBoxInPlace( box, button, center_x, center_y );
    } );
  }

  private openBoxInPlace( box: AdventureBox, button: UIImage, center_x: number, center_y: number ): void
  {
    button.setClickable( false );
    button.fadeOut( 150, performance.now(), Easing.EaseInQuad );
    AdventureBoxArt.swapCapToOpen( box.top, center_x, center_y, this.BOX_SCALE );
  }

  private buildLockedTapCatcher( content: UIElement, box: AdventureBox, floor_info: AdventureFloorInfo, center_x: number, center_y: number ): void
  {
    const main_h = LayoutScaler.heightFromDesign( AdventureBoxArt.MAIN_H * this.BOX_SCALE );
    const top_w = LayoutScaler.widthFromDesign( AdventureBoxArt.CLOSE_W * this.BOX_SCALE );
    const top_h = LayoutScaler.heightFromDesign( AdventureBoxArt.CLOSE_H * this.BOX_SCALE );
    const top_y = ( center_y - main_h ) - LayoutScaler.yFromDesign( AdventureBoxArt.CLOSE_OFFSET_Y * this.BOX_SCALE );

    const catcher = new UICanvasRect( this.ctx, center_x - top_w / 2, top_y, top_w, Math.max( top_h, center_y - top_y ), 'rgba(0, 0, 0, 0)' );
    content.addChild( catcher );

    catcher.setOnClick( () =>
    {
      const tooltip_content = floor_info.rewards.map( reward => ( {
        img_id: ADVENTURE_REWARD_ICONS[ reward.type ],
        count: reward.amount,
        amount_text: reward.type === "coin"
          ? String( reward.amount )
          : rewardAmountText( reward.type, reward.amount ),
      } ) );

      const { width: tooltip_w, height: tooltip_h } = AdventureTooltip.computeSize( tooltip_content.length );
      const single_row_h = AdventureTooltip.computeSize( 1 ).height;
      const tooltip_x = tooltip_w * this.TOOLTIP_TAIL_X_RATIO;

      const content_pos = content.getAbsolutePosition();
      const box_global_x = content_pos.x + center_x - tooltip_x;
      const box_global_y = content_pos.y + center_y + single_row_h / 2 - tooltip_h - box.main.getRect().getH() - box.top.getRect().getH();

      TooltipManager.openTooltip( new AdventureTooltip( TooltipManager.ctx, box_global_x, box_global_y, tooltip_w, tooltip_h, tooltip_x, tooltip_content ) );
    } );
  }

  private buildLevelBadge( content: UIElement, x: number, y: number, floor_info: AdventureFloorInfo, is_unlocked: boolean ): void
  {
    if ( floor_info.reward_collected )
    {
      const check = this.buildCompletedBadge( content, x, y );
      this.level_badges.set( floor_info.id, { content, x, y, elements: [ check ] } );
      return;
    }

    const level_text = floor_info.level.toString();
    const is_multi_digit = level_text.length > 1;
    const num_h = LayoutScaler.heightFromDesign( 55 );
    const num_w = LayoutScaler.widthFromDesign( is_multi_digit ? 58 : 50 );
    const text_x = x - LayoutScaler.xFromDesign( is_multi_digit ? 4 : 0 );
    const text_y = y;

    const base_style = {
      text: level_text,
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_weight: 900,
      font_size: LayoutScaler.textSizeFromDesign( is_multi_digit ? 36 : 41 ),
      min_font_size: LayoutScaler.textSizeFromDesign( 22 ),
      auto_wrap: false,
      fit_font_size: true,
    };

    const outlined_num = new UITextElement( this.ctx, text_x, text_y, num_w, num_h, {
      ...base_style,
      text_color: ColorScheme.white,
      outline_width: LayoutScaler.textBorderSizeFromDesign( is_multi_digit ? 7 : 9 ),
      outline_color: is_unlocked
        ? ColorScheme.adventure_level_marker_outline
        : ColorScheme.adventure_floor_text,
      shadow_blur: is_unlocked ? 0 : LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_y: is_unlocked ? 0 : LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent_30
    } );
    const plain_num = new UITextElement( this.ctx, text_x, text_y, num_w, num_h, { ...base_style, text_color: ColorScheme.white } );
    content.addChild( outlined_num );
    content.addChild( plain_num );
    this.level_badges.set( floor_info.id, { content, x, y, elements: [ outlined_num, plain_num ] } );
  }

  private replaceLevelBadgeWithCheck( floor_id: number ): void
  {
    const badge = this.level_badges.get( floor_id );
    if ( !badge ) return;

    for ( const element of badge.elements )
      badge.content.removeChild( element );

    const check = this.buildCompletedBadge( badge.content, badge.x, badge.y );
    this.level_badges.set( floor_id, { ...badge, elements: [ check ] } );
    badge.content.forceMakeShouldBeRendered();
  }

  private buildCompletedBadge( content: UIElement, x: number, y: number ): UIImage
  {
    const check_w = LayoutScaler.widthFromDesign( this.CHECK_W );
    const check_h = LayoutScaler.heightFromDesign( this.CHECK_H );
    const check = new UIImage( this.ctx, x - LayoutScaler.xFromDesign( 3 ), y + LayoutScaler.yFromDesign( 3 ), check_w, check_h, ImgID.IconAdventureCompleted );
    content.addChild( check );
    return check;
  }

  private boxStateFor( floor: AdventureFloorInfo ): AdventureBoxState
  {
    if ( !floor.passed ) return AdventureBoxState.Locked;
    return floor.reward_collected ? AdventureBoxState.Open : AdventureBoxState.Claimable;
  }

}
